import { isFulfilled } from "@reduxjs/toolkit";
import { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import AsyncContentSelector from "../../../../../common/components/functional/loadingSpinner/AsyncContentSelector";
import { CANDIDATE_INTRODUCTIONS_PAGE_SIZE } from "../../../../../common/components/functional/pagination/constants";
import { TablePaginationProps } from "../../../../../common/components/functional/pagination/types";
import isEmpty from "../../../../../common/helpers/isEmpty";
import {
  Hits,
  Paginated,
  Relation,
} from "../../../../../common/types/Paginated";
import { Nullable } from "../../../../../common/types/nullable";
import { useTablePagination } from "../../../../../hooks/useTablePagination";
import { Talent } from "../../../../talents/talentAPI";
import { fetchCandidateIntroductions } from "../../../../talents/talentsSlice";
import {
  ACTIVE_WITH_ORDER_INTRODUCTION_STATUSES,
  TalentIntroduction,
} from "../../../../talents/types/queries";
import EmptyStateText from "../../EmptyStateText";
import { CONTAINER_MIN_HEIGHT, INTRODUCTION_ROW_HEIGHT } from "../../constants";
import { TalentIntroductionRow } from "../../types";
import ShowAllButton from "./ShowAllButton";
import sortTalentIntroductionsByIntroducedAt from "./sortTalentIntroductionsByIntroducedAt";
import TalentIntroducedToCustomerRows from "./TalentIntroducedToCustomerRows";
import TalentIntroductionsToCustomerDialog from "./talentIntroductionsDialog/TalentIntroductionsToCustomerDialog";

type Props = {
  talent: Talent;
  previewMode?: boolean;
  introductions?: Paginated<TalentIntroduction[]>;
  onTotalChange: (total: number) => void;
};

const TalentIntroductionsWithOrderTab = ({
  talent,
  previewMode = false,
  introductions: previewIntroductions,
  onTotalChange,
}: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [hits, setHits] = useState<Hits>({
    relation: Relation.DEFAULT,
    value: 0,
  });
  const [cursor, setCursor] = useState<Nullable<string>>(null);
  const [introductions, setIntroductions] = useState<TalentIntroductionRow[]>(
    [],
  );
  const dispatch = useAppDispatch();
  const { workTitle: translatedWorkTitles } = useAppSelector(
    (state) => state.localized,
  );

  const dispatchFetchIntroductions = useCallback(
    (nextCursor?: string) => {
      setIsLoading(true);
      dispatch(
        fetchCandidateIntroductions({
          ...(nextCursor && { cursor: nextCursor }),
          talentId: talent.id,
          types: "WithOrder",
          pageSize: CANDIDATE_INTRODUCTIONS_PAGE_SIZE,
          statuses: ACTIVE_WITH_ORDER_INTRODUCTION_STATUSES,
        }),
      )
        .then((action) => {
          if (isFulfilled(action)) {
            const rows = action.payload.data.map((row) => ({
              ...row,
              workTitles: row.workTitleIds.map((id) => ({
                id,
                name: translatedWorkTitles[id] || id,
              })),
            }));
            setIntroductions((previous) =>
              sortTalentIntroductionsByIntroducedAt(
                nextCursor ? [...previous, ...rows] : rows,
              ),
            );
            setCursor(action.payload.cursor);
            setHits(action.payload.hits);
            onTotalChange(action.payload.hits.value);
          }
        })
        .finally(() => setIsLoading(false));
    },
    [dispatch, onTotalChange, talent.id, translatedWorkTitles],
  );

  useEffect(() => {
    if (previewMode) {
      const rows = (previewIntroductions?.data ?? []).map((introduction) => ({
        ...introduction,
        workTitles: introduction.workTitleIds.map((id) => ({
          id,
          name: translatedWorkTitles[id] || id,
        })),
      }));
      const previewHits = previewIntroductions?.hits ?? {
        relation: Relation.DEFAULT,
        value: 0,
      };

      setIntroductions(sortTalentIntroductionsByIntroducedAt(rows));
      setCursor(null);
      setHits(previewHits);
      onTotalChange(previewHits.value);
      return;
    }

    dispatchFetchIntroductions();
  }, [
    dispatchFetchIntroductions,
    onTotalChange,
    previewIntroductions,
    previewMode,
    translatedWorkTitles,
  ]);

  const { pageData, count, onPageChange, pageIndex } =
    useTablePagination<TalentIntroductionRow>({
      data: introductions,
      cursor,
      hits,
      pageSize: parseInt(CANDIDATE_INTRODUCTIONS_PAGE_SIZE, 10),
      preventDispatch: true,
      fetchNext: dispatchFetchIntroductions,
    });

  const pagination: TablePaginationProps = {
    count,
    onPageChange,
    pageIndex,
    pageSize: parseInt(CANDIDATE_INTRODUCTIONS_PAGE_SIZE, 10),
  };
  const displayShowAll = hits.value > 3;
  const containerMinHeight = isEmpty(introductions)
    ? CONTAINER_MIN_HEIGHT
    : `calc(${INTRODUCTION_ROW_HEIGHT} * ${displayShowAll ? 4 : hits.value + 1})`;

  return (
    <>
      <AsyncContentSelector
        isLoading={isLoading}
        emptyStateCondition={isEmpty(introductions)}
        emptyStateContent={<EmptyStateText />}
        ContainerProps={{ sx: { minHeight: containerMinHeight } }}
      >
        <TalentIntroducedToCustomerRows
          rows={introductions.slice(0, 3)}
          withOrder
        />
        {!previewMode && (
          <ShowAllButton
            visible={displayShowAll}
            disabled={isLoading}
            onClick={() => setDialogOpen(true)}
          />
        )}
      </AsyncContentSelector>
      {!previewMode && (
        <TalentIntroductionsToCustomerDialog
          open={dialogOpen}
          toggleDialog={() => setDialogOpen((open) => !open)}
          rows={pageData}
          isLoading={isLoading}
          pagination={pagination}
          withOrder
        />
      )}
    </>
  );
};

export default TalentIntroductionsWithOrderTab;
